import React, { useEffect, useState } from 'react';
import { Select, TextInput, Button, Group, Stack, Text, Flex, Checkbox, Tooltip, Box, Divider, Paper, NumberInput } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { Account } from './AccountCard';
import { createToast } from './Toast';
import { CalendarProvider, MessageKind, refactorCalendarName } from '../utils/common';
import API from '../utils/const';
import { useTranslation } from 'react-i18next';
import { EventVisibility, GoogleEventResponseStatus, SyncRule, Transparency } from "../utils/rule";
import { IconInfoCircle } from '@tabler/icons-react';
import { Accordion, Collapse } from 'react-bootstrap';
import { handleApiError } from '../utils/app';
import { RemindersInput } from './sync_rule/RemindersInput';
import { SyncRuleFormValues } from './sync_rule/interface';

interface SyncRuleModalProps {
    close: () => void;
    rule: SyncRule | null;
    accounts: Account[];
    successCallback: () => void;
}

function get_mapped_text(original: string, transformation: string) {
    return transformation.replace('%original%', original)
}

const statusToHuman = {
    [GoogleEventResponseStatus.needs_action]: 'Unanswered',
    [GoogleEventResponseStatus.declined]: 'Declined',
    [GoogleEventResponseStatus.tentative]: 'Tentative',
    [GoogleEventResponseStatus.accepted]: 'Accepted',
}

const visibilityToHuman = {
    [EventVisibility.DEFAULT]: 'Default',
    [EventVisibility.PRIVATE]: 'Private',
    [EventVisibility.CONFIDENTIAL]: 'Confidential',
    [EventVisibility.PUBLIC]: 'Public',
    [EventVisibility.KEEP_ORIGINAL]: 'Keep original visibility',
}

const transparencyToHuman = {
    [Transparency.keep_original]: 'Keep original',
    [Transparency.ignore_transparent]: 'Do not sync "free" events',
    [Transparency.ignore_opaque]: 'Do not sync "busy" events',
    [Transparency.set_to_transparent]: 'Set all events to "free"',
    [Transparency.set_to_opaque]: 'Set all events to "busy"',
}

const GOOGLE_ORANGE = '#ff9800'
const GOOGLE_BLUE = '#4285f4'

const GoogleEventComponent: React.FC<{ title: string, description: string, status: GoogleEventResponseStatus, color: string }> = ({ title, description, status, color }) => {
    let attributes: React.CSSProperties = { backgroundColor: color, color: 'white' }
    if (status === GoogleEventResponseStatus.declined) {
        attributes = { borderColor: color, backgroundColor: 'white', color: color }
    }
    if (status === GoogleEventResponseStatus.tentative) {
        attributes['backgroundImage'] = `repeating-linear-gradient(
            -45deg,
            ${attributes.backgroundColor} 8px,
            #ffffffdd 9px,
            #ffffffdd 25px
        )`
    }
    return (
        <Paper p="xs" withBorder style={{
            ...attributes
        }} radius='md'>
            <Text p={0} m={0} fw={600}>
                {title}
            </Text>
            <Text size="xs">
                {description}
            </Text>
        </Paper>
    )
}


const SyncRuleModal: React.FC<SyncRuleModalProps> = ({ close, rule, accounts, successCallback }) => {
    const { t } = useTranslation(['app']);
    const [loading, setLoading] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [regexError, setRegexError] = useState<Boolean | null>(null);
    const defaultTitle = 'Example Meeting'
    const defaultDescription = 'Discuss project updates and next steps'
    const [destinationTitle, setDestinationTitle] = useState(defaultTitle)
    const [destinationDescription, setDestinationDescription] = useState(defaultDescription)

    const defaultIsDestinationColourCompatible = (
        rule && rule.destination_uuid
        && calendarUUIDToProvider(rule?.destination_uuid) == CalendarProvider.GOOGLE 
    );
    const [isDestinationColourCompatible, setIsDestinationColourCompatible] = useState(defaultIsDestinationColourCompatible)

    const form = useForm<SyncRuleFormValues>({
        initialValues: {
            source: rule?.source || '',
            destination: rule?.destination || '',
            summary: rule?.summary || '%original%',
            description: rule?.description || '%original%',
            filterRule: rule?.filter_rule?.value,
            filterRuleType: rule?.filter_rule?.kind,
            filterRuleField: rule?.filter_rule?.field,
            statusTransform: rule?.status_transform || {
                needs_action: GoogleEventResponseStatus.needs_action,
                declined: GoogleEventResponseStatus.declined,
                tentative: GoogleEventResponseStatus.tentative,
                accepted: GoogleEventResponseStatus.accepted,
                deleted_declined: true,
            },
            visibility: rule?.visibility || EventVisibility.KEEP_ORIGINAL,
            color: rule?.color || '0',
            keep_location: rule?.keep_location?.toString() || 'false',
            transparency: rule?.transparency || Transparency.keep_original,
            bufferBefore: rule?.buffer?.minutes_before || 0,
            bufferAfter: rule?.buffer?.minutes_after || 0,
            includeAllDayEvents: rule?.include_all_day_events?.toString() || 'true',
            maintainEventId: rule?.maintain_event_id?.toString() || 'false',
            useReminders: rule?.reminders != null,
            reminders: rule?.reminders || {
                useDefault: false, // Default to using default reminders
                overrides: [],
            },
        },
        validate: {
            source: (value) => (!value ? t('dashboard.required-field') : null),
            destination: (value, values) =>
                !value ? t('dashboard.required-field') :
                    value === values.source ? t('dashboard.sync.different-calendars') : null,
            filterRuleField: (value, values) => {
                if ((values.filterRule || values.filterRuleType) && !value) {
                    return t('dashboard.required-field');
                }
                return null;
            },
            filterRuleType: (value, values) => {
                if ((values.filterRuleField || values.filterRule) && !value) {
                    return t('dashboard.required-field');
                }
                return null;
            },
            filterRule: (value, values) => {
                if ((values.filterRuleField || values.filterRuleType) && !value) {
                    return t('dashboard.required-field');
                }
                if (values.filterRuleType === 'regex') {
                    if (regexError) {
                        return <Text span size='xs'>{t('dashboard.sync.invalid-regex')} <a href="https://regex101.com/" target="_blank">regex101.com</a></Text>
                    }
                    return null;
                }
                return null;
            },
            bufferBefore: (value) => value < 0 ? 'Cannot be negative' : null,
            bufferAfter: (value) => value < 0 ? 'Cannot be negative' : null,
            reminders: (value) => {
                console.log(value)
                if (!value) {
                    return null;
                }
                // If useDefault is true, overrides should be empty
                if (value.useDefault && value.overrides.length > 0) {
                    return 'Cannot have both useDefault and overrides'
                }
                // If useDefault is false, at least one override is required
                if (!value.useDefault && value.overrides.length === 0) {
                    return 'At least one reminder is required when not using defaults'
                }
                return null;
            },
        },
        onValuesChange: (values) => {
            if (defaultIsDestinationColourCompatible){
                return ;
            }
            if (values.destination) {
                setIsDestinationColourCompatible(calendarUUIDToProvider(values.destination) == CalendarProvider.GOOGLE)
            }
        }
    });

    const handleSubmit = async (values: typeof form.values) => {
        setLoading(true);
        try {
            let content: Record<string, any> = {
                summary: values.summary,
                description: values.description,
                status_transform: values.statusTransform,
                visibility: values.visibility,
                color: values.color == '0' ? null : values.color,
                keep_location: values.keep_location === 'true',
                transparency: values.transparency,
                buffer: {
                    minutes_before: values.bufferBefore,
                    minutes_after: values.bufferAfter,
                },
                include_all_day_events: values.includeAllDayEvents === 'true',
                maintain_event_id: values.maintainEventId === 'true',
                // Add reminders handling
                reminders: values.useReminders ? values.reminders : null,
            }

            if (values.filterRuleField) {
                content.filter_rule = {
                    field: values.filterRuleField,
                    kind: values.filterRuleType,
                    value: values.filterRule,
                }
            }

            if (!rule) {
                // only include source and destination on POST request, not PATCH
                content['source_calendar_uuid'] = values.source
                content['destination_calendar_uuid'] = values.destination
            }

            const response = await fetch(`${API}/sync${rule ? `/${rule.uuid}` : ''}`, {
                method: rule ? 'PATCH' : 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(content),
            });

            if (!response.ok) {
                handleApiError(response, t, true)
                return;
            }
            successCallback();
            close();
        } catch (error) {
            console.error('Error saving sync rule:', error);
            createToast('An unexpected error occurred', MessageKind.Error);
        } finally {
            setLoading(false);
            // reset regex error
            setRegexError(null);
        }
    };

    function calendarUUIDToProvider(calendarUUID: string) {
        for (let account of accounts) {
            for (let calendar of account.calendars) {
                if (calendar.uuid === calendarUUID) {
                    return account.provider
                }
            }
        }
    }
    let sourceCalendars = []
    let destinationCalendars = []
    if (accounts) {
        let primaryCalendars = []
        for (let account of accounts) {
            for (let calendar of account.calendars) {
                if (calendar.primary) {
                    primaryCalendars.push(calendar.platform_id)
                }
            }
        }

        sourceCalendars = accounts.map(a => ({
            group: a.key,
            items: (
                a.calendars?.filter(
                    c => c.primary || !primaryCalendars.includes(c.platform_id)
                ).map(
                    c => ({ value: c.uuid, label: refactorCalendarName(c.name) })
                ) || []
            )
        }));

        destinationCalendars = accounts.map(a => ({
            group: a.key,
            items: a.calendars?.filter(c => !c.readonly).map(c => ({
                value: c.uuid,
                label: a.provider === CalendarProvider.ICS
                    ? `${refactorCalendarName(c.name)} (.ics calendars can't be destinations)`
                    : refactorCalendarName(c.name),
                disabled: a.provider === CalendarProvider.ICS
            })) || []
        }));
    }
    else {
        sourceCalendars = [{ value: rule.source, label: rule.source }]
        destinationCalendars = [{ value: rule.destination, label: rule.destination }]
    }

    const statusOptions = Object.values(GoogleEventResponseStatus).map(status => ({
        value: status,
        label: statusToHuman[status]
    }));

    const visibilityOptions = Object.values(EventVisibility).map(visibility => ({
        value: visibility,
        label: visibilityToHuman[visibility]
    }));

    const transparencyOptions = Object.values(Transparency).map(transparency => ({
        value: transparency,
        label: transparencyToHuman[transparency]
    }));

    let sourceAttributes = {}
    let destinationAttributes = {}
    if (rule) {
        sourceAttributes = {
            defaultValue: sourceCalendars[0].value,
            disabled: true
        }
        destinationAttributes = {
            defaultValue: destinationCalendars[0].value,
            disabled: true
        }
    }

    const colors = [
        { id: '0', background: '#ffffff', name: 'Default' },
        { id: '8', background: '#616161', name: 'Graphite' },
        { id: '9', background: '#3F51B5', name: 'Blueberry' },
        { id: '1', background: '#7986CB', name: 'Lavender' },
        { id: '3', background: '#8E24AA', name: 'Grape' },
        { id: '7', background: '#039BE5', name: 'Peacock' },
        { id: '10', background: '#0B8043', name: 'Basil' },
        { id: '2', background: '#33B679', name: 'Sage' },
        { id: '5', background: '#F6BF26', name: 'Banana' },
        { id: '6', background: '#F4511E', name: 'Tangerine' },
        { id: '4', background: '#E67C73', name: 'Flamingo' },
        { id: '11', background: '#D50000', name: 'Tomato' },
    ];

    useEffect(() => {
        if (regexError != null) {
            // Due to mantine not supporting async validation, 
            // we have to do the validation separately (onSubmitHook) and
            // then call this submit as a side effect
            form.onSubmit(handleSubmit)()
        }
    }, [regexError])

    const onSubmitHook = async () => {
        setRegexError(null)
        if (form.values.filterRuleType === 'regex') {
            try {
                const response = await fetch(`${API}/validate-regex`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ regex: form.values.filterRule })
                });
                if (response.status === 200) {
                    setRegexError(false)
                } else if (response.status === 400) {
                    setRegexError(true)
                } else {
                    createToast('An unexpected error occurred while validating the rule', MessageKind.Error)
                }
            } catch (error) {
                handleApiError(error, MessageKind.Error, true)
            }
        } else {
            setRegexError(false)
        }
    }

    return (
        <form onSubmit={(e) => { e.preventDefault(); onSubmitHook() }}>
            <Flex></Flex>
            <Stack gap="sm">
                <Text size='lg' c='dimmed'>{t('dashboard.sync.select-calendars')}</Text>
                <Select
                    placeholder={t("dashboard.sync.draft.source")}
                    data={sourceCalendars}
                    {...sourceAttributes}
                    {...form.getInputProps('source')}
                    searchable
                    required
                />

                <Select
                    placeholder={t("dashboard.sync.draft.destination")}
                    data={destinationCalendars}
                    defaultValue={destinationCalendars.find(c => c.default)?.value}
                    {...destinationAttributes}
                    {...form.getInputProps('destination')}
                    searchable
                    required
                />
                <Divider my='sm' />
                <Text size='lg' c='dimmed'>Decide how the calendar title and description are mapped</Text>
                <TextInput
                    label={<>
                        <Flex gap={0} justify="flex-start" align="center" mb='0%'>
                            <Text size='sm'>{t('dashboard.sync.label-custom-title')}</Text>
                            <Text size='sm' c='red' pl='2px' pr={'xs'}>*</Text>
                            <Tooltip label={t('dashboard.sync.title-tooltip')}
                                multiline
                                w={500}
                            >
                                <IconInfoCircle size={16} />
                            </Tooltip>
                        </Flex>
                    </>}
                    placeholder="Use %original% for the original event title"
                    {...form.getInputProps('summary')}
                    onChange={(e) => {
                        if (e.currentTarget.value === '') {
                            setDestinationTitle("Error - Can't be empty")
                        } else {
                            setDestinationTitle(get_mapped_text(defaultTitle, e.currentTarget.value))
                        }
                        if (form.getInputProps('summary').onChange)
                            form.getInputProps('summary').onChange(e)
                    }}
                />
                <TextInput
                    label={<>
                        <Flex gap="xs" justify="flex-start" align="center" mb='0%'>
                            <Text size='sm'>{t('dashboard.sync.label-custom-description')}</Text>
                            <Tooltip label={t('dashboard.sync.description-tooltip')}
                                multiline
                                w={500}
                            >
                                <IconInfoCircle size={16} />
                            </Tooltip>
                        </Flex>
                    </>}
                    placeholder="Use %original% for the original event description"
                    {...form.getInputProps('description')}
                    onChange={(e) => {
                        setDestinationDescription(get_mapped_text(defaultDescription, e.currentTarget.value))
                        if (form.getInputProps('description').onChange)
                            form.getInputProps('description').onChange(e)
                    }}
                />
                <Stack gap={0}>
                    <Text mt='md' size='md' c='dimmed'>{t('dashboard.sync.preview.title')}</Text>
                    <Group justify='space-between' gap='md'>
                        <Stack gap={0} miw='45%'>
                            <Text fw={600} size='sm' c='dimmed'>{t('dashboard.sync.preview.source')}</Text>
                            <GoogleEventComponent title={defaultTitle} description={defaultDescription} status={GoogleEventResponseStatus.accepted} color={GOOGLE_BLUE} />
                        </Stack>
                        <Stack gap={0} miw='45%'>
                            <Text fw={600} size='sm' c='dimmed'>{t('dashboard.sync.preview.destination')}</Text>
                            <GoogleEventComponent title={destinationTitle} description={destinationDescription} status={GoogleEventResponseStatus.accepted} color={GOOGLE_ORANGE} />

                        </Stack>
                    </Group>
                </Stack>
                <Divider my='sm' />
                <Text size='lg' c='dimmed'>Advanced settings</Text>
                <Accordion>
                    <Accordion.Item eventKey="general">
                        <Accordion.Header>General</Accordion.Header>
                        <Accordion.Body>
                            <Stack gap="sm">
                                <Select
                                    key='visibility'
                                    label={
                                        <Text>
                                            What do you want the visibility of the synced events to be?
                                        </Text>
                                    }
                                    defaultValue={EventVisibility.KEEP_ORIGINAL}
                                    data={visibilityOptions}
                                    allowDeselect={false}
                                    {...form.getInputProps('visibility')}
                                />
                                <Select
                                    disabled={!isDestinationColourCompatible}
                                    w='fit-content'
                                    label={t('dashboard.sync.label-color')}
                                    data={colors.map(color => ({
                                        value: color.id,
                                        label: color.name
                                    }))}
                                    renderOption={({ option, checked }) => (
                                        <Flex align="center" gap="sm" w="100%">
                                            <Box h={16} w={32} style={{ backgroundColor: colors.find(c => c.id === option.value)?.background, borderRadius: '4px' }}></Box>
                                            <Text size='sm'>{option.label}</Text>
                                        </Flex>
                                    )}
                                    allowDeselect={false}
                                    {...form.getInputProps('color')}
                                />
                                <Select
                                    w='fit-content'
                                    label={t('dashboard.sync.label-include-all-day-events')}
                                    data={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]}
                                    allowDeselect={false}
                                    {...form.getInputProps('includeAllDayEvents')}
                                />
                                <Select
                                    w='fit-content'
                                    label={t('dashboard.sync.label-location')}
                                    data={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]}
                                    allowDeselect={false}
                                    {...form.getInputProps('keep_location')}
                                />
                                <Select
                                    w='fit-content'
                                    label={t('dashboard.sync.label-free-busy')}
                                    data={transparencyOptions}
                                    allowDeselect={false}
                                    {...form.getInputProps('transparency')}
                                />
                                <Select
                                    w='fit-content'
                                    label={<Flex gap="xs" justify="flex-start" align="center" mb='0%'>
                                        <Text size='sm' fw={500}>{t('dashboard.sync.label-maintain-event-id')}</Text>
                                        <Tooltip label={t('dashboard.sync.maintain-event-id-tooltip')}
                                            multiline
                                            w={500}
                                        >
                                            <IconInfoCircle size={16} />
                                        </Tooltip>
                                    </Flex>}
                                    data={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]}
                                    allowDeselect={false}
                                    {...form.getInputProps('maintainEventId')}
                                />
                            </Stack>
                        </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="reminders">
                        <Accordion.Header>{t('dashboard.sync.reminders.title')}</Accordion.Header>
                        <Accordion.Body>
                            <RemindersInput form={form} />
                        </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="filter">
                        <Accordion.Header>{t('dashboard.sync.filter.title')}</Accordion.Header>
                        <Accordion.Body>
                            <Stack gap="sm">
                                <Text c='dimmed' size='sm'>{t('dashboard.sync.filter.description')}</Text>
                                <Flex gap="md" align="flex-start">
                                    <Select
                                        label={t('dashboard.sync.filter.field')}
                                        data={[
                                            { label: t('dashboard.sync.filter.field-options.summary'), value: 'summary' },
                                            { label: t('dashboard.sync.filter.field-options.description'), value: 'description' },
                                        ]}
                                        style={{ flexBasis: '20%' }}
                                        {...form.getInputProps('filterRuleField')}
                                        clearable
                                    />
                                    <Select
                                        label={t('dashboard.sync.filter.type')}
                                        data={[
                                            { value: 'contains', label: 'Contains word' },
                                            { value: 'regex', label: 'Regex' },
                                        ]}
                                        {...form.getInputProps('filterRuleType')}
                                        style={{ flexBasis: '25%' }}
                                        clearable
                                    />
                                    <TextInput
                                        label={t('dashboard.sync.filter.rule')}
                                        placeholder="Enter a filter rule"
                                        {...form.getInputProps('filterRule')}
                                        style={{ flexGrow: 1 }}
                                    />
                                </Flex>
                                <Button variant='outline' onClick={() => {
                                    form.setFieldValue('filterRule', '')
                                    form.setFieldValue('filterRuleField', null)
                                    form.setFieldValue('filterRuleType', null)
                                }} flex={'flex-start'} w='fit-content' miw='80px'>{t('dashboard.sync.filter.clear')}</Button>
                            </Stack>
                        </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="buffer">
                        <Accordion.Header>{t('dashboard.sync.buffer.title')}</Accordion.Header>
                        <Accordion.Body>
                            <Stack gap="sm">
                                <Text c='dimmed' size='sm'>{t('dashboard.sync.buffer.description')}</Text>
                                <Flex gap="md" align="flex-start">
                                    <NumberInput
                                        label={t('dashboard.sync.buffer.before')}
                                        {...form.getInputProps('bufferBefore')}
                                        min={0}
                                    />
                                    <NumberInput
                                        label={t('dashboard.sync.buffer.after')}
                                        {...form.getInputProps('bufferAfter')}
                                        min={0}
                                    />
                                </Flex>
                            </Stack>
                        </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="status">
                        <Accordion.Header>{t('dashboard.sync.status.title')}</Accordion.Header>
                        <Accordion.Body>
                            <Stack gap="sm" mt='md'>
                                <Text c='dimmed' size='sm'>{t('dashboard.sync.status.description')}</Text>
                                {Object.keys(GoogleEventResponseStatus).map((status) => (
                                    <Select
                                        key={status}
                                        label={
                                            <Text>
                                                {t('dashboard.sync.status.what-to-sync', { status: statusToHuman[GoogleEventResponseStatus[status]] })}
                                            </Text>
                                        }
                                        data={statusOptions}
                                        {...form.getInputProps(`statusTransform.${status}`)}

                                    />
                                ))}
                                <Checkbox
                                    label={t('dashboard.sync.label-declined')}
                                    {...form.getInputProps('statusTransform.deleted_declined', { type: 'checkbox' })}
                                />
                            </Stack>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
                <Collapse in={showAdvanced}>
                    <Box>
                        <Text size='lg' c='dimmed'>Filter events</Text>

                    </Box>
                </Collapse>
                <Group justify="flex-end" mt="md">
                    <Button variant="outline" onClick={close}>{t("common.cancel")}</Button>
                    <Button type="submit" loading={loading}>{t("common.save")}</Button>
                </Group>
            </Stack >
        </form >
    );
};

export default SyncRuleModal;
